import * as classnames from "classnames";

import { Button, ExamCountDownTimer, ProgressCircle, QuickHelp, Msg } from "..";
import { ApplicantExamModel } from "../../models";
import { formatDateTimeMinutes, getTimeZoneString } from "../../services/date";
import Message from "../../services/message";

/**
 * Prop interface
 */
export interface ApplicantExamStatusDisplayProps {
  exam?: ApplicantExamModel;
  onClickSubmit?: () => void;
  className?: string;
  children?: {};
}

/**
 * React Component
 */
export default function ApplicantExamStatusDisplay({
  exam = new ApplicantExamModel({} as ApplicantExamModel),
  onClickSubmit,
  className,
}: ApplicantExamStatusDisplayProps) {
  const requiredChallengeCount = exam.getRequiredChallengeCount();
  const optionalChallengeCount = exam.getMinimumOptionalChallengeCount();
  const challengeStatsText = `${Message.getMessageByKey(
    "exam.requiredChallenges",
  )}: ${requiredChallengeCount}, ${Message.getMessageByKey(
    "exam.optionalChallenges",
  )}: ${optionalChallengeCount}`;

  // MEMO: the finish count for the optional challenge set should be less or equal than `numberChallengesToTake`.
  const challengeFinishStats = exam.getChallengeFinishStats();
  const finishChallengeCount =
    challengeFinishStats.required +
    Math.min(optionalChallengeCount, challengeFinishStats.optional) +
    challengeFinishStats.random;

  const actualFinishChallengeCount =
    challengeFinishStats.required +
    challengeFinishStats.optional +
    challengeFinishStats.random;

  const rootStyle = classnames("code-c-applicant-exam-status-display", {
    [`${className}`]: Boolean(className),
  });

  return (
    <div className={rootStyle}>
      {requiredChallengeCount + optionalChallengeCount > 0 && (
        <div className="code-c-applicant-exam-status-display__header">
          <ProgressCircle
            className="code-c-applicant-exam-status-display__header-left"
            size="medium"
            value={Math.floor(
              (finishChallengeCount /
                (requiredChallengeCount + optionalChallengeCount)) *
                100,
            )}
          />
          <div className="code-c-applicant-exam-status-display__header-right">
            <Msg id="common.progress" />
            <div className="code-c-applicant-exam-status-display__score-container">
              <span className="code-c-applicant-exam-status-display__score">
                {finishChallengeCount}
              </span>
              <span className="code-c-applicant-exam-status-display__score-base">
                /{requiredChallengeCount + optionalChallengeCount}
              </span>
              {exam.hasOptionalChallengeSet() && (
                <QuickHelp text={challengeStatsText} placement="top-end" />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="code-c-applicant-exam-status-display__body">
        <div>
          <Msg id="common.deadline" />:
        </div>
        <div>
          <strong>{formatDateTimeMinutes(exam.deadline)}</strong>{" "}
          <small>({getTimeZoneString()})</small>
        </div>
      </div>
      <div className="code-c-applicant-exam-status-display__footer">
        <Button
          type="warning"
          onClick={onClickSubmit}
          disabled={!exam.canSubmit() || actualFinishChallengeCount === 0}
          ariaLabel="Submit Exam"
        >
          <Msg id="button.submitExam" />
        </Button>
        <ExamCountDownTimer
          className="code-c-applicant-exam-status-display__time-left"
          thresholdSeconds={20 * 60}
        />
      </div>
    </div>
  );
}
